import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PublicPage } from "layouts";
import * as styles from "./styles";
import {
  IsAuthenticatedContext,
  passLastUpdatedContext,
  CsrfTokenContext,
} from "auth";
import { Button } from "components/atoms/Button";
import { ErrorMessage } from "components/atoms/ErrorMessage";
import { TextInput } from "components/atoms/TextInput";
import { useMutation } from "@apollo/client";
import {
  GET_LOGIN,
  GET_OTP,
  UPDATE_PHONE_NUMBER,
  VERIFY_OTP,
  VerifyOtpResponse,
  VerifyOtpVars,
  LoginResponse,
  LoginVars,
  OtpResponse,
  OtpVars,
  UpdatePhoneNumberResponse,
  UpdatePhoneNumberVars,
} from "./queries";
import {
  Alert,
  Snackbar,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
} from "@mui/material";
import PasswordEyeIcon from "assets/svg/Eyeicon";
import {
  PasswordWrapper,
  ReceiveText,
  OverlayLoader,
} from "layouts/PublicPage/styles";
import axios from "axios";
import { MONETA_API_BASE_URL } from "config";

enum LoginStep {
  CREDENTIALS,
  MFA_METHOD,
  MFA_CODE,
}

export const Login = () => {
  const [getLogin, { data, error }] = useMutation<LoginResponse, LoginVars>(
    GET_LOGIN
  );
  const [getOtp, { loading: isLoadingGetOtp }] = useMutation<
    OtpResponse,
    OtpVars
  >(GET_OTP);

  const [updatePhoneNumber] = useMutation<
    UpdatePhoneNumberResponse,
    UpdatePhoneNumberVars
  >(UPDATE_PHONE_NUMBER);

  const [verifyOtp, { loading: isLoadingVerifyOtp }] = useMutation<
    VerifyOtpResponse,
    VerifyOtpVars
  >(VERIFY_OTP);

  const [showMutationError, setShowMutationError] = useState<boolean>(false);
  const [isAuthenticated, setIsAuthenticated] = useContext(
    IsAuthenticatedContext
  );
  const [csrfToken, setCsrfToken] = useContext(CsrfTokenContext);
  const [passwordLastUpdated, setPasslastUpdated] = useContext(
    passLastUpdatedContext
  );
  const localUser = localStorage.getItem("username") || "";
  const navigate = useNavigate();
  const [email, setEmail] = useState<string>("");
  const [username, setUsername] = useState<string>(localUser);
  const [password, setPassword] = useState<string>("");
  const [showErrors, setShowErrors] = useState<boolean>(false);
  const [visible, setVisible] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<LoginStep>(
    LoginStep.CREDENTIALS
  );
  const [mfaMethod, setMfaMethod] = useState<"email" | "sms">("email");
  const [mfaCode, setMfaCode] = useState<string>("");
  const [mfaAttempts, setMfaAttempts] = useState<number>(0);
  const [isLockedOut, setIsLockedOut] = useState<boolean>(false);
  const [codeExpiryTime, setCodeExpiryTime] = useState<number | null>(null);
  const [canRequestNewCode, setCanRequestNewCode] = useState<boolean>(false);
  const [timeUntilNewCode, setTimeUntilNewCode] = useState<number>(60);
  const [currentCodeId, setcurrentCodeId] = useState<string>("");

  const [tempCredentials, setTempCredentials] = useState<{
    email: string;
    password: string;
  }>({
    email: "",
    password: "",
  });
  const [recaptchaVerifier, setRecaptchaVerifier] = useState<any>(null);

  useEffect(() => {
    if (error) {
      setShowErrors(true);
      setShowMutationError(true);
    }
  }, [error]);

  useEffect(() => {
    const lastPass = data?.login?.lastPass;
    const today = Date.now();
    if (Math.abs((today - Number(lastPass)) / (1000 * 60 * 60 * 24)) > 60) {
      localStorage.setItem("passValid", "false");
      setPasslastUpdated(false);
    }
  }, [passwordLastUpdated, navigate, isAuthenticated, username]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (codeExpiryTime) {
      timer = setInterval(() => {
        const now = Date.now();
        if (now >= codeExpiryTime) {
          setCodeExpiryTime(null);
          setMfaCode("");
        }
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [codeExpiryTime]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (timeUntilNewCode > 0 && !canRequestNewCode) {
      timer = setInterval(() => {
        setTimeUntilNewCode((prev) => {
          if (prev <= 1) {
            setCanRequestNewCode(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [timeUntilNewCode, canRequestNewCode]);

  const handleCredentialsSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setShowErrors(false);

    try {
      const result = await getLogin({
        variables: { username: email, password: password },
      });
      if (result?.data) {
        setCurrentStep(LoginStep.MFA_METHOD);
        setMfaAttempts(0);
      }
    } catch (error) {
      console.error("Failed to login user");
      setShowMutationError(true);
    }
  };

  useEffect(() => {
    if (currentStep === LoginStep.MFA_METHOD) {
      setTempCredentials({ email, password });
      setEmail(email);
      setPassword("");
    }
  }, [currentStep]);

  const generateSixDigitCode = async () => {
    setMfaCode("");
    setShowErrors(false);
    try {
      const result = await getOtp({
        variables: {
          userId: tempCredentials.email,
          method: mfaMethod,
        },
      });

      if (result?.data) {
        setCurrentStep(LoginStep.MFA_CODE);
        setMfaAttempts(0);
      }
    } catch (error) {
      setShowMutationError(true);
    }
  };

  const generateMFACode = async () => {
    // const newCodeId = Date.now().toString();
    // setCurrentCodeId(newCodeId);
    setCodeExpiryTime(Date.now() + 5 * 60 * 1000); // 5 minutes
    setCanRequestNewCode(false);
    setTimeUntilNewCode(60);
    setMfaCode("");
    setShowErrors(false);
    generateSixDigitCode();
  };

  const handleMFAMethodSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    generateMFACode();
  };

  const emailCode = localStorage.getItem("emailCode");
  const smsCode = localStorage.getItem("smsCode");

  const handleMFACodeSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();

    const result = await verifyOtp({
      variables: {
        userId: tempCredentials.email,
        code: mfaCode,
      },
    }); 
    if (result.data?.verifyOtp.done.toString() == "true") {
      const info = await axios.get(`${MONETA_API_BASE_URL}/csrf/token`, {
        withCredentials: true,
      });
      if (info?.data) {
        setIsAuthenticated(true);

        localStorage.setItem("username", btoa(tempCredentials.email));
        localStorage.setItem("isLoggedIn", "true");
        setUsername(tempCredentials.email);
        setUsername(email);
        setPasslastUpdated(true);
        navigate("/patients");
      }
    } else {
      setMfaAttempts((prev) => {
        const newAttempts = prev + 1;
        if (newAttempts >= 6) {
          setIsLockedOut(true);
        }
        return newAttempts;
      });
      setShowErrors(true);
    }
  };

  const handleBackToCredentials = () => {
    setCurrentStep(LoginStep.CREDENTIALS);
    setShowErrors(false);
  };

  if (isLockedOut) {
    return (
      <PublicPage
        title={
          <styles.Spacer>
            <div>Oops! Too many failed attempts.</div>
          </styles.Spacer>
        }
      >
        <styles.Container>
          <styles.ErrorText>
            Your account has been locked. Please contact your administrator to
            have your account unlocked.
          </styles.ErrorText>
        </styles.Container>
      </PublicPage>
    );
  }

  const renderCredentialsForm = () => (
    <styles.Form onSubmit={handleCredentialsSubmit}>
      <TextInput
        label="Email address"
        type="email"
        placeholder="you@domain.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        hasError={showErrors}
        required
        fullWidth
      />
      <PasswordWrapper>
        <TextInput
          id="myInput"
          label="Password"
          type={visible ? "text" : "password"}
          placeholder="****"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          hasError={showErrors}
          required
          fullWidth
        />
        <PasswordEyeIcon onClick={() => setVisible(!visible)} />
      </PasswordWrapper>
      <ErrorMessage isVisible={showErrors}>{error?.message || ""}</ErrorMessage>
      <styles.FormActions>
        <Button type="submit" fullWidth={true}>
          Continue
        </Button>
      </styles.FormActions>
    </styles.Form>
  );

  const renderMFAMethodForm = () => (
    <div>
      <ReceiveText>
        How would you like to receive the security code?
      </ReceiveText>
      <styles.Form onSubmit={handleMFAMethodSubmit}>
        <RadioGroup
          value={mfaMethod}
          onChange={(e) => setMfaMethod(e.target.value as "email" | "sms")}
        >
          <FormControlLabel
            value="email"
            control={<Radio />}
            label={`Email me`}
          />
          <FormControlLabel value="sms" control={<Radio />} label="Text me" />
        </RadioGroup>
        <styles.ButtonActions>
          <Button type="button" onClick={handleBackToCredentials}>
            Back
          </Button>
          <Button type="submit" onClick={handleMFAMethodSubmit}>
            Send Security Code
          </Button>
        </styles.ButtonActions>
      </styles.Form>
    </div>
  );

  const renderMFACodeForm = () => (
    <styles.Form onSubmit={handleMFACodeSubmit}>
      {showErrors && (
        <styles.ErrorText>
          The security code you entered does not match the one we sent. If you
          requested a code more than once, make sure you&apos;re entering the
          latest code.
          {mfaAttempts < 4 ? (
            "Please try again"
          ) : (
            <>
              <br />
              <br />
              This is your {mfaAttempts + 1}th attempt. You have{" "}
              {6 - mfaAttempts} more attempts before your account is locked.
            </>
          )}
        </styles.ErrorText>
      )}
      <TextInput
        label="Security code"
        type="text"
        value={mfaCode}
        maxLength={6}
        onChange={(e) => {
          const newValue = e.target.value;
          if (/^\d{0,6}$/.test(newValue)) {
            setMfaCode(newValue);
          }
        }}
        hasError={showErrors}
        required
        fullWidth
      />
      {/* {codeExpiryTime && <p>Code expires in: {Math.ceil((codeExpiryTime - Date.now()) / 1000 / 60)} minutes</p>} */}
      {!canRequestNewCode && (
        <p>Can request new code in: {timeUntilNewCode} seconds</p>
      )}
      <Button
        type="button"
        onClick={generateMFACode}
        disabled={!canRequestNewCode}
      >
        Send New Code
      </Button>
      <div style={{ margin: "20px 0" }}>
        {mfaAttempts > 0 && (
          <Alert severity="warning">Failed attempts: {mfaAttempts}/6</Alert>
        )}
      </div>
      <styles.ButtonActions>
        <Button
          type="button"
          onClick={() => {
            setCurrentStep(LoginStep.MFA_METHOD);
            setShowErrors(false);
            setMfaCode("");
          }}
        >
          Back
        </Button>
        <Button type="submit">Log In</Button>
      </styles.ButtonActions>
    </styles.Form>
  );

  return (
    <PublicPage
      title={
        currentStep === LoginStep.CREDENTIALS ? (
          <div>
            Welcome back!
            <br /> Sign in to your moneta account
          </div>
        ) : currentStep === LoginStep.MFA_METHOD ? (
          <div>
            <div>Confirm Your Identity</div>
            <span style={{ fontSize: "17px" }}>
              We want to know it's really you.
            </span>
          </div>
        ) : (
          <div>
            <div>Enter Security Code</div>
            <span style={{ fontSize: "17px" }}>
              We sent you a security code. When you get it, please enter it
              below. The code will expire 5 minutes after you receive it.
            </span>
          </div>
        )
      }
    >
      <styles.Container>
        {currentStep === LoginStep.CREDENTIALS && renderCredentialsForm()}
        {currentStep === LoginStep.MFA_METHOD && renderMFAMethodForm()}
        {currentStep === LoginStep.MFA_CODE && renderMFACodeForm()}

        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          open={showMutationError}
          onClose={() => setShowMutationError(false)}
        >
          <Alert onClose={() => setShowMutationError(false)} severity="error">
            {currentStep === LoginStep.MFA_METHOD
              ? "Please wait 60 seconds before requesting a new code"
              : "Failed to login user."}
          </Alert>
        </Snackbar>
        {(isLoadingGetOtp || isLoadingVerifyOtp) && (
          <OverlayLoader>
            <CircularProgress />
          </OverlayLoader>
        )}
      </styles.Container>
    </PublicPage>
  );
};

export default Login;
